import React, { useState, useRef, useEffect } from "react";
import styles from "./Header.module.css";
import { useTranslation } from "react-i18next";
import { FaPhone, FaCheckCircle } from "react-icons/fa";
import { trackLead } from "../../../utils/track";
import { savePotentialCustomer } from "../../../utils/leadApi";
import {
  getOnboardingUrl,
  setStoredHeroPhone,
  getStoredHeroPhone,
  isPaidVisitor,
  isHeroLeadAlreadySaved,
  markHeroLeadSaved,
} from "../../../utils/portalUrl";
import { isValidLeadPhone } from "../../../utils/phoneValidation";
import DemoModal from "../../../Components/DemoModal/DemoModal";
import { TEMP_CRM_PIVOT } from "../../../config/tempCrmPivot";

import arIpad from "../../../assets/ar_main.png";
import arPhone from "../../../assets/ar.png";
import heIpad from "../../../assets/he_main.png";
import hePhone from "../../../assets/he.png";
import enIpad from "../../../assets/en_main.png";
import enPhone from "../../../assets/en.png";

const IMAGES = {
  ar: { ipad: arIpad, phone: arPhone },
  he: { ipad: heIpad, phone: hePhone },
  en: { ipad: enIpad, phone: enPhone },
};

// Stricter than "≥ 9 digits": full libphonenumber validation + anti-
// spam pattern heuristic. Stops us firing the Google Ads "lead"
// conversion (and saving a potential-customer row) for obvious
// garbage like 999999999 or 123456789, which were burning ad budget.
const isPhoneComplete = (val) => isValidLeadPhone(val);

const Header = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const imgs = IMAGES[lang] || IMAGES.en;

  // Rehydrate from localStorage so a visitor who typed a phone, navigated to
  // another page, and came back still sees it in the field — and so any
  // subsequent "Get Started" CTA forwards the phone to onboarding.
  const [phone, setPhone] = useState(() => getStoredHeroPhone());
  const [showModal, setShowModal] = useState(false);
  const savedRef = useRef(false);
  const debounceRef = useRef(null);

  // localStorage write moved to the onChange handler below (synchronous
  // with each keystroke). The previous useEffect-based write ran AFTER
  // React paint, which means `getOnboardingUrl("hero")` — evaluated
  // during the same render that contained the user's latest digit —
  // would read the PREVIOUS localStorage value. A user who typed the
  // last digit and clicked "Start Now" immediately would navigate to
  // the portal with a phone missing the final digit. Writing
  // synchronously in onChange eliminates that one-render lag.

  // Auto-save lead when phone looks complete.
  //
  // `savedRef` was previously the only dedupe guard, but it's a
  // useRef — fresh per mount. Any return-visit to the company site
  // (the phone is restored from localStorage on mount) would re-fire
  // a duplicate save and spam the staff WhatsApp group with the same
  // lead over and over. The localStorage-backed `isHeroLeadAlreadySaved`
  // makes the dedupe survive across page loads / navigations.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (isPhoneComplete(phone) && !savedRef.current && !isHeroLeadAlreadySaved(phone)) {
      debounceRef.current = setTimeout(() => {
        savedRef.current = true;
        markHeroLeadSaved(phone);
        savePotentialCustomer(phone);
        // GTM lead event — Google Ads Lead conversion. Phone is hashed inside trackLead.
        trackLead({ phone, source: 'hero_phone_form_auto' });
      }, 800);
    }
    return () => clearTimeout(debounceRef.current);
  }, [phone]);

  const handleContactMe = () => {
    if (!phone.trim()) return;

    // Only save the lead AND fire the Google Ads conversion when the
    // phone passes real validation. Invalid input (999999999, abcdef,
    // 7-digit garbage) still lets the visitor click through to the
    // portal — the portal has its own stricter input where they'll be
    // guided to a correct number. We just don't burn an ad-conversion
    // event on it here.
    const phoneIsValid = isValidLeadPhone(phone);
    if (phoneIsValid) {
      const wasAlreadySaved = savedRef.current || isHeroLeadAlreadySaved(phone);
      if (!wasAlreadySaved) {
        savedRef.current = true;
        markHeroLeadSaved(phone);
        savePotentialCustomer(phone);
        // Only fire the GTM lead event for fresh saves so Google
        // Ads doesn't see this phone twice on a return visit.
        trackLead({ phone, source: 'hero_phone_form_submit' });
      }
    }

    // Paid traffic shortcut: when the visitor came from an ad
    // (utm_source / gclid / gbraid / wbraid present in the current URL
    // OR in the per-tab attribution captured on landing), skip the
    // Book Demo / Try Free / WhatsApp modal and send them straight to
    // onboarding. Without this, ~90% of phone-form submitters get to
    // the modal and never click anything past it — the modal optimises
    // for "Stylon team member shows them around" rather than "ad-click
    // visitor self-serves". Organic visitors still see the modal so
    // the Book Demo flow stays reachable from the hero.
    if (isPaidVisitor()) {
      window.location.href = getOnboardingUrl('hero');
      return;
    }

    // Show success modal
    setShowModal(true);
  };

  return (
    <div
      id="home"
      className={`${styles.headerWrap} ${styles.fadeInDown}`}
    >
      <div className={`container ${styles.parent}`}>
        <section className={styles.child1}>
          <h1>{t(TEMP_CRM_PIVOT ? "tempPivot.header.title" : "header.title")}</h1>
          <p>{t(TEMP_CRM_PIVOT ? "tempPivot.header.description" : "header.description")}</p>

          {/* Phone input CTA */}
          <div className={styles.phoneInputRow}>
            <div className={styles.phoneInputWrap}>
              <FaPhone className={styles.phoneIcon} />
              <input
                type="tel"
                className={styles.phoneInput}
                placeholder={t("callMe.placeholder")}
                value={phone}
                onChange={(e) => {
                  const v = e.target.value;
                  setPhone(v);
                  // Synchronous so the very next render (which builds
                  // every CTA's href via getOnboardingUrl → reads
                  // localStorage) sees the digit the user just typed.
                  // useEffect was running one render too late.
                  setStoredHeroPhone(v);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleContactMe()}
                dir="ltr"
              />
              <button
                type="button"
                className={`${styles.contactBtn} ${phone.trim() ? styles.contactBtnVisible : styles.contactBtnHidden}`}
                onClick={handleContactMe}
              >
                {t("callMe.button")}
              </button>
            </div>
          </div>

          <div className={styles.btmbox}>
            <a
              href={getOnboardingUrl("hero")}
              className={styles.ctaLink}
            >
              <span className={styles.ctaButton}>
                {t("nav.startNow")}
              </span>
            </a>
            <p className={styles.noCreditCard}>{t("header.noCreditCard")}</p>
          </div>
        </section>

        <section className={styles.child2}>
          <div className={styles.mockupWrapper}>
            <div className={styles.ipadFrame}>
              <div className={styles.ipadGlass} />
              <img src={imgs.ipad} alt="app dashboard" className={styles.ipadImg} />
            </div>
            <div className={styles.phoneFrame}>
              <div className={styles.phoneGlass} />
              <img src={imgs.phone} alt="app mobile" className={styles.phoneImg} />
            </div>
          </div>
        </section>
      </div>

      {/* Trust strip sits below the mockup (and below the parent flex
          row on desktop) so it reads as the closing reassurance line
          of the hero — not a CTA modifier. Centered, plain white text
          on the gradient backdrop. */}
      <div className={styles.trustStripWrap}>
        <div className={styles.trustStrip}>
          <FaCheckCircle className={styles.trustCheck} aria-hidden="true" />
          <span>{t("header.trustedBy")}</span>
        </div>
      </div>

      {showModal && (
        <DemoModal phone={phone} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};

export default Header;
